import { useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { logger } from '../utils/logger';

/**
 * Hook for WebSocket functionality
 * Provides methods to subscribe to events and emit events
 */
export function useWebSocketEvents() {
  const { client, isConnected } = useWebSocket();
  const listenersRef = useRef<Map<string, Set<(...args: any[]) => void>>>(new Map());

  /**
   * Subscribe to a WebSocket event
   */
  const subscribe = useCallback(
    (event: string, callback: (...args: any[]) => void) => {
      if (!client) {
        logger.warn('Cannot subscribe: WebSocket client not available', { event });
        return () => {}; // Return no-op unsubscribe function
      }

      // Track listeners for cleanup
      if (!listenersRef.current.has(event)) {
        listenersRef.current.set(event, new Set());
      }
      listenersRef.current.get(event)!.add(callback);

      // Subscribe to event
      client.on(event, callback);

      // Return unsubscribe function
      return () => {
        const eventListeners = listenersRef.current.get(event);
        if (eventListeners) {
          eventListeners.delete(callback);
          if (eventListeners.size === 0) {
            listenersRef.current.delete(event);
          }
        }
        client.off(event, callback);
      };
    },
    [client]
  );

  /**
   * Emit an event to the server
   */
  const emit = useCallback(
    (event: string, data: any) => {
      if (!client) {
        logger.warn('Cannot emit: WebSocket client not available', { event });
        return;
      }

      if (!isConnected) {
        logger.warn('Cannot emit: WebSocket not connected', { event });
        return;
      }

      client.emit(event, data);
    },
    [client, isConnected]
  );

  // Cleanup all listeners on unmount
  useEffect(() => {
    return () => {
      if (client) {
        listenersRef.current.forEach((callbacks, event) => {
          callbacks.forEach((callback) => {
            client.off(event, callback);
          });
        });
        listenersRef.current.clear();
      }
    };
  }, [client]);

  return {
    subscribe,
    emit,
    isConnected,
  };
}

/**
 * Hook to subscribe to a specific event
 */
export function useWebSocketEvent<T = any>(
  event: string,
  callback: (data: T) => void,
  deps: React.DependencyList = []
) {
  const { subscribe } = useWebSocketEvents();

  useEffect(() => {
    const unsubscribe = subscribe(event, callback as (...args: any[]) => void);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, subscribe, ...deps]);
}
